import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { CheckCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { LinkedInJobsTable } from "./LinkedInJobsTable";
import { LinkedInJobMatchModal } from "./LinkedInJobMatchModal";
import { linkedInApi } from "@/services/linkedin-api";
import { type LinkedInJob } from "@/types/linkedin";
import { cn } from "@/lib/utils";
import { toUserError } from "@/lib/api-error";

// Status que pedem ação humana. É a fila.
const STATUS_PENDENTES = "review_ready,awaiting_match,failed";

type Filtro = "todos" | "review_ready" | "awaiting_match" | "failed";

const FILTROS: { valor: Filtro; label: string }[] = [
  { valor: "todos", label: "Tudo" },
  { valor: "review_ready", label: "Aguardando revisão" },
  { valor: "awaiting_match", label: "Aguardando associação" },
  { valor: "failed", label: "Com falha" },
];

/**
 * Só o que o classificador liberaria sozinho: são os candidatos naturais a
 * aplicar em massa, sem abrir um por um.
 */
function elegiveisParaAplicarEmMassa(jobs: LinkedInJob[]): LinkedInJob[] {
  return jobs.filter(
    (job) =>
      job.status === "review_ready" &&
      job.freelancer_id &&
      (job.auto_review?.reasons ?? []).some(
        (razao) => razao.code === "auto_apply_desligado",
      ),
  );
}

/** Dias parados, pro rótulo de "está esperando há muito tempo". */
function diasParado(job: LinkedInJob): number {
  const ms = Date.now() - new Date(job.created_at).getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

export function LinkedInFilaRevisao() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [matchJob, setMatchJob] = useState<LinkedInJob | null>(null);

  const filaQuery = useQuery({
    queryKey: ["linkedin-jobs", "fila", filtro],
    queryFn: () =>
      linkedInApi.listJobs({
        status: filtro === "todos" ? STATUS_PENDENTES : filtro,
        mais_antigos_primeiro: true,
        acionaveis: true,
        limit: 200,
      }),
  });
  const jobs = useMemo(() => filaQuery.data?.jobs ?? [], [filaQuery.data]);
  const emMassa = useMemo(() => elegiveisParaAplicarEmMassa(jobs), [jobs]);
  const maisAntigo = jobs.length > 0 ? diasParado(jobs[0]) : 0;

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["linkedin-jobs"] });
    queryClient.invalidateQueries({ queryKey: ["linkedin-batches"] });
    queryClient.invalidateQueries({ queryKey: ["linkedin-resumo"] });
  };

  const aplicarEmMassa = useMutation({
    mutationFn: (jobIds: string[]) => linkedInApi.applyBatch(jobIds),
    onSuccess: (resultado) => {
      if (resultado.falhas.length === 0) {
        toast({
          title: "Aplicados",
          description: `${resultado.aplicados.length} perfis foram atualizados.`,
        });
      } else {
        toast({
          variant: resultado.aplicados.length > 0 ? "default" : "destructive",
          title: `${resultado.aplicados.length} aplicados · ${resultado.falhas.length} falharam`,
          description: resultado.falhas
            .slice(0, 3)
            .map((falha) => falha.reason)
            .join("\n"),
        });
      }
      invalidar();
    },
    onError: (erro: Error) => {
      toast({
        title: "Erro ao aplicar",
        description: toUserError(erro).description,
        variant: "destructive",
      });
    },
  });

  const reprocess = useMutation({
    mutationFn: (job: LinkedInJob) => linkedInApi.reextract(job.id),
    onSuccess: () => {
      toast({ title: "Reprocessando", description: "A extração foi reiniciada." });
      invalidar();
    },
    onError: (erro: Error) => {
      toast({ title: "Erro ao reprocessar", description: toUserError(erro).description, variant: "destructive" });
    },
  });

  const rescrape = useMutation({
    mutationFn: (freelancerId: string) => linkedInApi.scrapeBatch([freelancerId]),
    onSuccess: () => {
      toast({ title: "Buscando novamente", description: "O perfil entrou na fila." });
      invalidar();
    },
    onError: (erro: Error) => {
      toast({ title: "Erro ao buscar", description: toUserError(erro).description, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTROS.map((opcao) => (
            <Button
              key={opcao.valor}
              size="sm"
              variant={filtro === opcao.valor ? "secondary" : "ghost"}
              onClick={() => setFiltro(opcao.valor)}
            >
              {opcao.label}
            </Button>
          ))}
          {maisAntigo >= 7 && filtro === "todos" && (
            <Badge
              variant="outline"
              className={cn("ml-1", "border-amber-200 bg-amber-50 text-amber-800")}
            >
              o mais antigo espera há {maisAntigo} dias
            </Badge>
          )}
        </div>

        {emMassa.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                className="gap-1.5"
                disabled={aplicarEmMassa.isPending}
              >
                {aplicarEmMassa.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCheck className="h-4 w-4" />
                )}
                Aplicar os {emMassa.length} sem ressalva
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Aplicar {emMassa.length} perfis de uma vez?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  São os perfis em que a revisão não encontrou nenhuma ressalva:
                  emprego atual em empresa conhecida ou perfil que se declara
                  freelancer. Os dados extraídos vão para o cadastro de cada
                  freelancer e ficam registrados no histórico, de onde dá para
                  conferir depois.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => aplicarEmMassa.mutate(emMassa.map((job) => job.id))}
                >
                  Aplicar {emMassa.length}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        {filaQuery.isPending ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {filtro === "todos"
              ? "Nada pendente. Tudo que foi extraído já passou por revisão."
              : "Nada com esse filtro."}
          </div>
        ) : (
          <LinkedInJobsTable
            jobs={jobs}
            onAssociate={(job) => setMatchJob(job)}
            onReview={(job) => navigate(`/linkedin/jobs/${job.id}/revisar`)}
            onReprocess={(job) => reprocess.mutate(job)}
            onRescrape={(job) => job.freelancer_id && rescrape.mutate(job.freelancer_id)}
          />
        )}
      </div>

      <LinkedInJobMatchModal job={matchJob} onClose={() => setMatchJob(null)} />
    </div>
  );
}
